import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileText, MoreVertical, PenTool, Plus } from 'lucide-react';
import type { AppKind, AppToolMeta } from '@awwyeah/shared';
import { noteDocName, sketchDocName } from '@awwyeah/shared';
import { useUser } from '../auth';
import {
  AudienceBadge,
  AudiencePicker,
  saveDocAudience,
  useDocAudience,
  type AudienceValue,
} from '../sharing';
import { useHouseholdRoster } from './useHouseholdRoster';
import { Sheet } from './components/Sheet';
import { createAppToolMeta } from './appToolOps';
import { useAppToolRegistries, type RegisteredAppTool } from './useAppToolRegistries';
// Same arrangement, same reasoning (2026-08-18): trackers get no 7th bottom-tab either, so
// their management surface is published from `trackers`'s `index.ts` and embedded here.
import { TrackersSection } from '../trackers';

function contentPath(meta: Pick<AppToolMeta, 'id' | 'kind'>): string {
  return meta.kind === 'note' ? `/apps/note/${meta.id}` : `/apps/sketch/${meta.id}`;
}

function contentDocName(meta: Pick<AppToolMeta, 'id' | 'kind'>): string {
  return meta.kind === 'note' ? noteDocName(meta.id) : sketchDocName(meta.id);
}

function relativeTime(iso: string): string {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function ToolCard({
  tool,
  audience,
  memberCount,
  onOpenMenu,
}: {
  tool: AppToolMeta;
  audience: AudienceValue['audience'];
  memberCount: number;
  onOpenMenu: () => void;
}): JSX.Element {
  const navigate = useNavigate();
  const Icon = tool.kind === 'note' ? FileText : PenTool;

  return (
    <div
      className="relative flex flex-col gap-2 rounded-md border border-line bg-surface p-4"
      data-testid="app-tool-card"
      data-tool-id={tool.id}
      data-tool-kind={tool.kind}
    >
      <button
        type="button"
        aria-label={`Open ${tool.title}`}
        data-testid="open-app-tool"
        onClick={() => navigate(contentPath(tool))}
        className="absolute inset-0 z-0 rounded-md"
      />
      <div className="pointer-events-none relative z-10 flex items-center justify-between">
        <span
          className="flex h-11 w-11 items-center justify-center rounded-full bg-raised text-fg-2"
          aria-hidden="true"
        >
          <Icon size={20} />
        </span>
        <button
          type="button"
          aria-label={`${tool.title} options`}
          data-testid="app-tool-menu-button"
          onClick={(e) => {
            e.stopPropagation();
            onOpenMenu();
          }}
          className="pointer-events-auto relative z-10 flex h-11 w-11 items-center justify-center text-fg-3"
        >
          <MoreVertical size={18} />
        </button>
      </div>
      <div className="pointer-events-none relative z-10 min-w-0">
        <div className="flex min-w-0 items-center gap-2 text-base font-semibold text-fg">
          <span className="min-w-0 flex-1 truncate">
            {tool.emoji ? `${tool.emoji} ` : ''}
            {tool.title}
          </span>
          <span className="flex-shrink-0">
            <AudienceBadge audience={audience} memberCount={memberCount} />
          </span>
        </div>
        <div className="text-xs text-fg-3">Updated {relativeTime(tool.updatedAt)}</div>
      </div>
    </div>
  );
}

function NewToolSheet({
  open,
  kind,
  onClose,
  userId,
  registries,
  members,
  onCreated,
}: {
  open: boolean;
  kind: AppKind | null;
  onClose: () => void;
  userId: string;
  registries: ReturnType<typeof useAppToolRegistries>;
  members: readonly { id: string; name: string; color: string; avatarEmoji?: string }[];
  onCreated: (meta: AppToolMeta, audience: AudienceValue) => void;
}): JSX.Element {
  const navigate = useNavigate();
  const [audience, setAudience] = useState<AudienceValue>({ audience: 'family', memberIds: [] });
  const [title, setTitle] = useState('');

  const create = (): void => {
    if (!kind) return;
    const trimmed = title.trim() || (kind === 'note' ? 'Untitled note' : 'Untitled sketch');
    const meta = createAppToolMeta({
      kind,
      title: trimmed,
      userId,
      emoji: kind === 'note' ? '📝' : '🎨',
    });
    const target =
      audience.audience === 'family'
        ? registries.familyMap
        : (registries.personalMap ?? registries.familyMap);
    target.set(meta.id, meta);
    registries.mirror(meta);
    onCreated(meta, audience);
    setTitle('');
    setAudience({ audience: 'family', memberIds: [] });
    onClose();
    navigate(contentPath(meta));
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={kind === 'sketch' ? 'New sketch' : 'New note'}
      tall
      testId="new-app-tool-sheet"
    >
      <div className="flex flex-col gap-4 pb-2">
        <div className="flex flex-col gap-1.5">
          <label
            className="text-xs font-semibold uppercase tracking-wide text-fg-3"
            htmlFor="new-app-tool-title"
          >
            Title
          </label>
          <input
            id="new-app-tool-title"
            data-testid="new-app-tool-title-input"
            className="h-12 rounded border-2 border-line-strong bg-bg px-3 text-base text-fg"
            placeholder={kind === 'sketch' ? 'Family doodle' : 'Grocery ideas'}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && create()}
            autoFocus
          />
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-fg-3">
            Who can see this
          </span>
          <AudiencePicker
            value={audience}
            onChange={setAudience}
            members={members}
            testId="new-app-tool-audience"
          />
        </div>
        <button
          type="button"
          data-testid="new-app-tool-create"
          onClick={create}
          className="h-12 rounded-md bg-inverse text-base font-semibold text-inverse-fg"
        >
          Create
        </button>
      </div>
    </Sheet>
  );
}

function AudienceSheet({
  tool,
  members,
  onClose,
  onApplied,
}: {
  tool: AppToolMeta;
  members: readonly { id: string; name: string; color: string; avatarEmoji?: string }[];
  onClose: () => void;
  onApplied: (tool: AppToolMeta, value: AudienceValue) => void;
}): JSX.Element {
  const docName = contentDocName(tool);
  const state = useDocAudience(docName);
  const [draft, setDraft] = useState<AudienceValue | null>(null);
  const [saving, setSaving] = useState(false);
  const value: AudienceValue = draft ?? { audience: state.audience, memberIds: state.memberIds };

  const apply = async (): Promise<void> => {
    setSaving(true);
    const ok = await state.save(value);
    setSaving(false);
    if (!ok) return;
    onApplied(tool, value);
    onClose();
  };

  return (
    <Sheet open onClose={onClose} title="Who can see this" tall testId="app-tool-audience-sheet">
      <div className="flex flex-col gap-4 pb-2">
        <p className="text-sm text-fg-2">
          &ldquo;{tool.title}&rdquo; — private tools don&apos;t show up for anyone else, not even
          the title.
        </p>
        {state.loading ? (
          <p className="text-sm text-fg-3">Checking…</p>
        ) : (
          <AudiencePicker
            value={value}
            onChange={setDraft}
            members={members}
            disabled={!state.canManage}
            testId="app-tool-audience-picker"
          />
        )}
        {state.error && (
          <p className="text-sm text-danger" data-testid="app-tool-audience-error">
            Couldn&apos;t change that ({state.error}). Nothing was altered.
          </p>
        )}
        {state.canManage && (
          <button
            type="button"
            data-testid="save-app-tool-audience"
            disabled={saving || state.loading}
            onClick={() => void apply()}
            className="h-12 rounded-md bg-inverse text-base font-semibold text-inverse-fg disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        )}
      </div>
    </Sheet>
  );
}

function ToolActionsSheet({
  tool,
  members,
  onClose,
  onRename,
  onDelete,
  onAudienceApplied,
}: {
  tool: AppToolMeta | null;
  members: readonly { id: string; name: string; color: string; avatarEmoji?: string }[];
  onClose: () => void;
  onRename: (tool: AppToolMeta, title: string) => void;
  onDelete: (tool: AppToolMeta) => void;
  onAudienceApplied: (tool: AppToolMeta, value: AudienceValue) => void;
}): JSX.Element {
  const [renaming, setRenaming] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [draftTitle, setDraftTitle] = useState(tool?.title ?? '');

  const close = (): void => {
    setRenaming(false);
    setConfirmingDelete(false);
    setSharing(false);
    onClose();
  };

  if (!tool) return <></>;

  if (sharing) {
    return (
      <AudienceSheet tool={tool} members={members} onClose={close} onApplied={onAudienceApplied} />
    );
  }

  if (confirmingDelete) {
    return (
      <Sheet open onClose={close} title={`Delete "${tool.title}"?`} testId="delete-app-tool-sheet">
        <div className="flex flex-col gap-3 pb-2">
          <p className="text-sm text-fg-2">
            Removes it from the Apps tab for everyone. The{' '}
            {tool.kind === 'note' ? 'note' : 'sketch'} itself isn&apos;t re-editable afterwards.
          </p>
          <button
            type="button"
            data-testid="confirm-delete-app-tool"
            className="h-12 rounded-md bg-danger text-base font-semibold text-surface"
            onClick={() => {
              onDelete(tool);
              close();
            }}
          >
            Delete
          </button>
          <button
            type="button"
            className="h-12 rounded-md border-2 border-line-strong text-base font-semibold text-fg"
            onClick={() => setConfirmingDelete(false)}
          >
            Keep it
          </button>
        </div>
      </Sheet>
    );
  }

  if (renaming) {
    return (
      <Sheet open onClose={close} title="Rename" testId="rename-app-tool-sheet">
        <div className="flex flex-col gap-3 pb-2">
          <input
            data-testid="rename-app-tool-input"
            className="h-12 rounded border-2 border-line-strong bg-bg px-3 text-base text-fg"
            value={draftTitle}
            autoFocus
            onChange={(e) => setDraftTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && draftTitle.trim()) {
                onRename(tool, draftTitle.trim());
                close();
              }
            }}
          />
          <button
            type="button"
            data-testid="save-rename-app-tool"
            disabled={!draftTitle.trim()}
            className="h-12 rounded-md bg-inverse text-base font-semibold text-inverse-fg disabled:opacity-50"
            onClick={() => {
              onRename(tool, draftTitle.trim());
              close();
            }}
          >
            Save
          </button>
        </div>
      </Sheet>
    );
  }

  return (
    <Sheet open onClose={close} title={tool.title} testId="app-tool-actions-sheet">
      <div className="flex flex-col">
        <button
          type="button"
          data-testid="rename-app-tool-action"
          className="flex h-14 items-center text-left text-base font-medium text-fg"
          onClick={() => {
            setDraftTitle(tool.title);
            setRenaming(true);
          }}
        >
          Rename
        </button>
        <button
          type="button"
          data-testid="share-app-tool-action"
          className="flex h-14 items-center border-t border-line text-left text-base font-medium text-fg"
          onClick={() => setSharing(true)}
        >
          Who can see this
        </button>
        <button
          type="button"
          data-testid="delete-app-tool-action"
          className="flex h-14 items-center border-t border-line text-left text-base font-medium text-danger"
          onClick={() => setConfirmingDelete(true)}
        >
          Delete
        </button>
      </div>
    </Sheet>
  );
}

export function AppsIndexPage(): JSX.Element {
  const { user } = useUser();
  const registries = useAppToolRegistries();
  const roster = useHouseholdRoster();
  const [newKind, setNewKind] = useState<AppKind | null>(null);
  const [menuToolId, setMenuToolId] = useState<string | null>(null);
  const [audiences, setAudiences] = useState<Record<string, AudienceValue>>({});

  const otherMembers = [...roster.members.values()]
    .filter((member) => member.id !== user?.id)
    .map((member) => ({
      id: member.id,
      name: member.name,
      color: member.color,
      avatarEmoji: member.avatarEmoji,
    }));

  const entries: RegisteredAppTool[] = registries.tools;
  const menuTool = entries.find((e) => e.meta.id === menuToolId)?.meta ?? null;

  const audienceFor = (entry: RegisteredAppTool): AudienceValue => {
    const known = audiences[entry.meta.id];
    if (known) return known;
    if (entry.sharedWithMe) return { audience: 'members', memberIds: [] };
    return { audience: entry.personal ? 'private' : 'family', memberIds: [] };
  };

  const mapFor = (toolId: string): typeof registries.familyMap | null => {
    const where = registries.registryOf(toolId);
    if (where === 'shared-with-me') return null;
    if (where === 'personal' && registries.personalMap) return registries.personalMap;
    return registries.familyMap;
  };

  const touch = (tool: AppToolMeta, patch: Partial<AppToolMeta>): void => {
    if (!user) return;
    const next: AppToolMeta = {
      ...tool,
      ...patch,
      updatedBy: user.id,
      updatedAt: new Date().toISOString(),
    };
    mapFor(tool.id)?.set(tool.id, next);
    registries.mirror(next);
  };

  const deleteTool = (tool: AppToolMeta): void => {
    // Registry remove + tombstone: the content doc (`note:<id>`/`sketch:<id>`) is left
    // orphaned rather than deleted — accepted for v1, see docs/DECISIONS.md 2026-08-02.
    mapFor(tool.id)?.delete(tool.id);
  };

  const applyAudience = (tool: AppToolMeta, value: AudienceValue): void => {
    registries.placeInRegistry(tool, value.audience);
    setAudiences((current) => ({ ...current, [tool.id]: value }));
  };

  if (!user) return <></>;

  return (
    <div className="flex flex-col gap-6 py-4" data-testid="apps-index-page">
      <header className="flex flex-col gap-1">
        <Link to="/settings" className="flex h-8 items-center text-sm text-fg-3">
          ‹ Settings
        </Link>
        <h2 className="font-display text-2xl tracking-tight text-fg">Apps &amp; experiments</h2>
      </header>

      {/* Folded by default (Dan, 2026-09-08): the schedule import and trackers are the
          daily tools; games open on demand. */}

      <TrackersSection />

      <h3 className="text-lg font-bold text-fg">Notes &amp; sketches</h3>

      {/* Registries load from IndexedDB asynchronously (offline-first); an empty `entries`
          before that finishes isn't "no notes or sketches", it's "hasn't hydrated yet" —
          most visible right after an update-triggered reload. Render nothing until
          `locallyLoaded`, rather than flashing the empty state over a household that has
          plenty (WS-update-reload audit). */}
      {!registries.locallyLoaded ? null : entries.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-md border border-dashed border-line py-10 text-center">
          <p className="text-sm text-fg-3">
            No notes or sketches yet. Start one below — everyone in the family can see it unless you
            say otherwise.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3" data-testid="app-tool-grid">
          {entries.map((entry) => (
            <ToolCard
              key={entry.meta.id}
              tool={entry.meta}
              audience={audienceFor(entry).audience}
              memberCount={audienceFor(entry).memberIds.length}
              onOpenMenu={() => setMenuToolId(entry.meta.id)}
            />
          ))}
        </div>
      )}

      <div className="mt-auto flex items-center gap-3 pt-2">
        <button
          type="button"
          data-testid="new-note-button"
          className="flex h-12 flex-1 items-center justify-center gap-2 rounded-md border-2 border-line-strong text-sm font-semibold text-fg"
          onClick={() => setNewKind('note')}
        >
          <Plus size={16} /> New note
        </button>
        <button
          type="button"
          data-testid="new-sketch-button"
          className="flex h-12 flex-1 items-center justify-center gap-2 rounded-md border-2 border-line-strong text-sm font-semibold text-fg"
          onClick={() => setNewKind('sketch')}
        >
          <Plus size={16} /> New sketch
        </button>
      </div>

      <NewToolSheet
        open={newKind !== null}
        kind={newKind}
        onClose={() => setNewKind(null)}
        userId={user.id}
        registries={registries}
        members={otherMembers}
        onCreated={(meta, value) => {
          setAudiences((current) => ({ ...current, [meta.id]: value }));
          if (value.audience !== 'family') {
            void saveDocAudience(contentDocName(meta), {
              audience: value.audience,
              memberIds: value.memberIds,
            }).catch(() => undefined);
          }
        }}
      />
      <ToolActionsSheet
        tool={menuTool}
        members={otherMembers}
        onClose={() => setMenuToolId(null)}
        onRename={(tool, title) => touch(tool, { title })}
        onDelete={deleteTool}
        onAudienceApplied={applyAudience}
      />
    </div>
  );
}
